import { createContext, Fragment, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { buildDefaultContent } from "@/content/defaultContent";

export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContentContext = createContext(null);

export const deepMerge = (base, override) => {
  if (override === undefined || override === null) {
    return base;
  }
  if (Array.isArray(base)) {
    if (!Array.isArray(override)) {
      return base;
    }
    return override.map((item, index) =>
      index < base.length ? deepMerge(base[index], item) : item
    );
  }
  if (typeof base === "object" && base !== null) {
    if (typeof override !== "object" || Array.isArray(override)) {
      return base;
    }
    const merged = { ...base };
    Object.keys(override).forEach((key) => {
      merged[key] = key in base ? deepMerge(base[key], override[key]) : override[key];
    });
    return merged;
  }
  return override;
};

export const txt = (node) =>
  node && typeof node.text === "string" ? node.text : "";

export const sz = (node) =>
  node && node.size ? { fontSize: `${node.size}px` } : undefined;

export const lines = (node) => {
  const parts = txt(node).split("\n");
  return parts.map((part, index) => (
    <Fragment key={index}>
      {part}
      {index < parts.length - 1 && (
        <>
          {" "}
          <br />
        </>
      )}
    </Fragment>
  ));
};

export const mediaUrl = (url) => {
  if (!url) {
    return "";
  }
  if (url.startsWith("/")) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

// True when the app is rendered inside the admin panel's live preview iframe.
// In that mode content comes from the parent window instead of the API so the
// admin sees unsaved edits.
export const IS_PREVIEW = (() => {
  try {
    return (
      new URLSearchParams(window.location.search).get("preview") === "1" &&
      window.parent !== window
    );
  } catch (err) {
    return false;
  }
})();

export const ContentProvider = ({ children }) => {
  const defaults = useMemo(() => buildDefaultContent(), []);
  const [content, setContent] = useState(null);

  useEffect(() => {
    if (IS_PREVIEW) {
      const handleMessage = (event) => {
        if (event.data && event.data.type === "admin-preview-content") {
          setContent(deepMerge(defaults, event.data.content || {}));
        }
      };
      window.addEventListener("message", handleMessage);
      window.parent.postMessage({ type: "admin-preview-ready" }, "*");
      return () => window.removeEventListener("message", handleMessage);
    }

    let active = true;
    axios
      .get(`${API_BASE}/site-content`)
      .then((res) => {
        if (active) {
          setContent(deepMerge(defaults, res.data.content || {}));
        }
      })
      .catch(() => {
        if (active) {
          setContent(defaults);
        }
      });
    return () => {
      active = false;
    };
  }, [defaults]);

  if (!content) {
    return null;
  }

  return (
    <ContentContext.Provider value={content}>{children}</ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);
